import { useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import PalmettoSideMenu from '@zawarski/palmetto-react-side-menu';
import {
  BookOpen,
  CalendarDays,
  ChartScatter,
  CreditCard,
  FolderOpen,
  House,
  Lightbulb,
  LogOut,
  MessageCircleQuestion,
  ReceiptText,
  Square,
  SquareDashed,
  User,
} from 'lucide-react';
import { APP_VERSION, PROGRAM_NAME, USER_GUIDE_URL } from '@constants/index';
import { clearToken } from '@auth/token';
import ContactSupportDialog from '@components/ContactSupportDialog';
import { useAccess } from '@hooks/useAccess';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { SET_NAV_OPEN, SIGN_OUT } from '@store/app/app.reducer';
import { navOpenSelector } from '@store/app/app.selector';

/** The menu shape the package draws (it exports the component, not its types). */
type SideMenuItems = ComponentProps<typeof PalmettoSideMenu>['menuItems'];

export interface SideMenuItem {
  key: string;
  name: string;
  path: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  /** Full-access users only (legacy: Capability Targets and Milestones). */
  fullOnly?: boolean;
}

/** The legacy drawer menu, in legacy order with the legacy icons (DrawerMenu.jsx). */
export const SIDE_MENU_ITEMS: SideMenuItem[] = [
  { key: 'dashboard', name: 'Home', path: '/', startIcon: <House /> },
  {
    key: 'lcert',
    name: 'Annual Letters of Certification',
    path: '/lcert',
    startIcon: <ReceiptText />,
  },
  {
    key: 'requirements',
    name: 'Exercises',
    path: '/exercises',
    startIcon: <SquareDashed />,
  },
  { key: 'equipment', name: 'Equipment', path: '/equipment', startIcon: <CreditCard /> },
  {
    key: 'redistribution',
    name: 'Redistribution Matrix',
    path: '/redistribution',
    startIcon: <ChartScatter />,
  },
  {
    key: 'filelibrary',
    name: 'File Library',
    path: '/filelibrary',
    startIcon: <FolderOpen />,
  },
  { key: 'training', name: 'Training', path: '/training', startIcon: <Lightbulb /> },
  {
    key: 'capabilities',
    name: 'Capability Targets',
    path: '/capabilities',
    startIcon: <Square />,
    fullOnly: true,
  },
  {
    key: 'milestones',
    name: 'Milestones',
    path: '/milestones',
    startIcon: <CalendarDays />,
    fullOnly: true,
  },
];

/**
 * Paths of the user / guide / contact-support / log-out rows under the menu. The package hands the
 * chosen row's `path` to `onMenuClick`, so these rows are told apart by theirs; the `#` keeps them
 * from ever matching a route.
 */
const SECTION_PATHS = {
  user: '#user',
  userGuide: '#user-guide',
  contactSupport: '#contact-support',
  logout: '#logout',
} as const;

export type SideMenuAction = 'navigate' | 'close' | 'userGuide' | 'contactSupport' | 'logout';

/** What choosing a row does: the rows under the menu run their action, every entry opens its route. */
export const sideMenuAction = (path: string): SideMenuAction => {
  switch (path) {
    case SECTION_PATHS.user:
      return 'close';
    case SECTION_PATHS.userGuide:
      return 'userGuide';
    case SECTION_PATHS.contactSupport:
      return 'contactSupport';
    case SECTION_PATHS.logout:
      return 'logout';
    default:
      return 'navigate';
  }
};

const isItemActive = (path: string, pathname: string) =>
  path === '/' ? pathname === '/' || pathname === '/letters' : pathname.startsWith(path);

/**
 * The `activePath` to hand the package on this route. The package marks an entry active when
 * `activePath.includes(entry.key)`, which the raw pathname does not fit: '/' holds no 'dashboard',
 * '/exercises' no 'requirements', and '/exercises/plants/7/capabilities' would mark Capability
 * Targets. So the route resolves to its entry here and the package gets that entry's key, or ''
 * on a route no entry owns.
 */
export const sideMenuActivePath = (pathname: string) =>
  SIDE_MENU_ITEMS.find((item) => isItemActive(item.path, pathname))?.key ?? '';

/**
 * The entries this user may open, then the user / guide / contact-support / log-out rows and the
 * version line.
 */
export const buildSideMenuItems = ({
  isFull,
  userName,
}: {
  isFull: boolean;
  userName: string;
}): SideMenuItems => ({
  menu: SIDE_MENU_ITEMS.filter((item) => !item.fullOnly || isFull),
  section: [
    { key: 'user', name: userName, path: SECTION_PATHS.user, startIcon: <User /> },
    {
      key: 'userGuide',
      name: 'View user guide',
      path: SECTION_PATHS.userGuide,
      endIcon: <BookOpen className='rep:text-[#2961fd]' />,
    },
    {
      key: 'contactSupport',
      name: 'Contact Support',
      path: SECTION_PATHS.contactSupport,
      endIcon: <MessageCircleQuestion className='rep:text-[#2961fd]' />,
    },
    {
      key: 'logout',
      name: 'Log out',
      path: SECTION_PATHS.logout,
      endIcon: <LogOut className='rep:text-[#eb021d]' />,
    },
  ],
  version: { name: `Version ${APP_VERSION}` },
});

/**
 * The REP side menu: @zawarski/palmetto-react-side-menu, the drawer rep-ui used (DrawerMenu.jsx).
 * It slides in from the left with the logo, program title, the "Menu" list, and the user / guide /
 * contact-support / log-out rows with the version underneath. Opened by the hamburger in every page
 * header; closes on Escape, a backdrop click, or after choosing a row.
 */
const SideMenu = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const open = useAppSelector(navOpenSelector);
  const { isFull, userName } = useAccess();
  const [contactSupportOpen, setContactSupportOpen] = useState(false);

  const close = () => dispatch(SET_NAV_OPEN(false));

  const handleMenuClick = (path: string) => {
    switch (sideMenuAction(path)) {
      case 'logout':
        // SIGN_OUT closes the menu with the rest of the session, and the page reloads.
        clearToken();
        dispatch(SIGN_OUT());
        window.location.assign('/');
        return;
      case 'userGuide':
        window.open(USER_GUIDE_URL, '_blank', 'noopener,noreferrer');
        break;
      case 'contactSupport':
        // The drawer closes below and the form opens over the current page.
        setContactSupportOpen(true);
        break;
      case 'navigate':
        navigate(path);
        break;
      case 'close':
        break;
    }
    close();
  };

  return (
    <>
      <PalmettoSideMenu
        srcLogo='/images/launcher.png'
        programTitle={PROGRAM_NAME}
        menuItems={buildSideMenuItems({ isFull, userName })}
        isDrawerOpen={open}
        activePath={sideMenuActivePath(pathname)}
        // REP's menu has no sub-menus.
        activeSubMenu={false}
        deactivateSubMenu={() => undefined}
        onDrawerClose={close}
        onMenuClick={handleMenuClick}
      />
      <ContactSupportDialog open={contactSupportOpen} onOpenChange={setContactSupportOpen} />
    </>
  );
};

export default SideMenu;
